import { Controller } from '@hotwired/stimulus';

// Controls the single DGD retention modal. Three actions:
//   openCreate() — reset the form to POST /contract/:id/dgd/retentions
//   openEdit({id, label, amountTtc, comment, kind}) — swap the form to
//     PATCH /contract/:id/dgd/retentions/:id and pre-fill the fields. The row
//     carries the amount POSITIVE and its kind (« retention » or
//     « regularization », stored negative server-side): the toggle holds the sense.
//
// One form is shared by create and edit and keeps its state between openings
// (#968): openCreate puts the toggle back on « retention », or the line after
// editing a régularisation would be saved with the wrong sign.
//   confirmDelete() — submit the same form as DELETE after a native confirm.
//     Reuses the existing turbo:submit-end hook to close the modal on success.
//
// The underlying modal UX (focus trap, ESC to close) is provided by
// the generic `modal` controller attached via Ui::ModalComponent.
export default class extends Controller {
  static targets = [
    'form',
    'labelInput',
    'amountInput',
    'commentInput',
    'kindInput',
    'submit',
    'title',
    'deleteButton',
  ];
  static values = {
    createUrl: String,
    updateUrlTemplate: String, // e.g. "/projects/7/contracts/42/dgd/retentions/__ID__"
  };

  connect() {
    // Close the modal when the form submits successfully (2xx). On a 4xx the
    // controller renders the inline error partial via turbo_stream and the modal
    // stays open with the user's input intact.
    this._onSubmitEnd = (event) => {
      if (event.target !== this.formTarget) return;
      if (event.detail?.success) this._closeModal();
    };
    this.formTarget.addEventListener('turbo:submit-end', this._onSubmitEnd);

    // Hide the delete button by default (overrides ButtonComponent's
    // inline-flex class without fighting Tailwind utility ordering).
    if (this.hasDeleteButtonTarget) this.deleteButtonTarget.style.display = 'none';
  }

  disconnect() {
    if (this._onSubmitEnd) {
      this.formTarget.removeEventListener('turbo:submit-end', this._onSubmitEnd);
    }
  }

  openCreate() {
    this._setTitle('Ajouter une ligne');
    this.formTarget.action = this.createUrlValue;
    this.formTarget.method = 'post';
    this._setMethodOverride(null);
    this.labelInputTarget.value = '';
    this.amountInputTarget.value = '';
    this.commentInputTarget.value = '';
    this._setKind('retention');
    this.submitTarget.textContent = 'Ajouter';
    if (this.hasDeleteButtonTarget) this.deleteButtonTarget.style.display = 'none';
    this._openModal();
  }

  openEdit(event) {
    const { id, label, amountTtc, comment, kind } = event.params;
    this._setTitle('Modifier la ligne');
    this.formTarget.action = this.updateUrlTemplateValue.replace('__ID__', id);
    this.formTarget.method = 'post';
    this._setMethodOverride('patch');
    this.labelInputTarget.value = label || '';
    this.amountInputTarget.value = amountTtc || '';
    this.commentInputTarget.value = comment || '';
    this._setKind(kind || 'retention');
    this.submitTarget.textContent = 'Enregistrer';
    if (this.hasDeleteButtonTarget) this.deleteButtonTarget.style.display = '';
    this._openModal();
  }

  // Submit the existing form as DELETE. Reuses the open form's action
  // (already pointing to the resource URL via openEdit's URL substitution),
  // the existing turbo:submit-end hook closes the modal on 2xx success.
  // Server responds with a Turbo Stream that replaces dgd-retentions-section
  // and dgd-balance-section.
  //
  // `_method=delete` must never outlive this call: left on the shared form, the
  // next « Enregistrer » goes out as a DELETE and destroys the retention (#968).
  // Two ways it used to survive, both closed here:
  //   - a cleared `required` field (libellé, montant) blocked the send, since
  //     `requestSubmit()` runs constraint validation synchronously: a deletion
  //     validates no field, so `noValidate` is raised for the call;
  //   - the DELETE failed (network, 422 once the DGD left draft) and the modal
  //     stayed open: the override goes back to `patch` as soon as
  //     `requestSubmit()` returns. Turbo builds the request body synchronously
  //     inside the submit event (`FormSubmission` constructor), so the DELETE
  //     already in flight keeps its method.
  confirmDelete(event) {
    event?.preventDefault();
    if (!window.confirm('Confirmer la suppression de cette ligne ?')) return;
    const noValidate = this.formTarget.noValidate;
    this._setMethodOverride('delete');
    this.formTarget.noValidate = true;
    try {
      this.formTarget.requestSubmit();
    } finally {
      this.formTarget.noValidate = noValidate;
      this._setMethodOverride('patch');
    }
  }

  // Setting `.checked` restyles the segment (`has-[:checked]:`) without a change event.
  _setKind(kind) {
    this.kindInputTargets.forEach((radio) => {
      radio.checked = radio.value === kind;
    });
  }

  _setTitle(text) {
    // Update the actual visible accessible title (rendered by Ui::ModalComponent).
    const visible = document.getElementById('dgd-retention-modal-title');
    if (visible) visible.textContent = text;
    // Mirror to the Stimulus target if present (defensive fallback, sr-only).
    if (this.hasTitleTarget) this.titleTarget.textContent = text;
  }

  _setMethodOverride(method) {
    let hidden = this.formTarget.querySelector('input[name="_method"]');
    if (!method) {
      hidden?.remove();
      return;
    }
    if (!hidden) {
      hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = '_method';
      this.formTarget.prepend(hidden);
    }
    hidden.value = method;
  }

  _openModal() {
    const modal = document.getElementById('dgd-retention-modal');
    // Ui::ModalComponent's modal controller exposes itself on the DOM node and
    // emits `modal:open` (it does NOT listen for it). Open via the exposed API
    // directly, with a window.openModal fallback.
    if (modal?.modalController) {
      modal.modalController.open();
    } else if (typeof window.openModal === 'function') {
      window.openModal('dgd-retention-modal');
    }
  }

  _closeModal() {
    const modal = document.getElementById('dgd-retention-modal');
    if (modal?.modalController) {
      modal.modalController.close();
    } else if (typeof window.closeModal === 'function') {
      window.closeModal('dgd-retention-modal');
    }
  }
}
